<script setup>
// assets/project/src/components/Orders.vue
import { ref, reactive, onMounted, computed } from 'vue'
import {
	EyeOutlined,
	EditOutlined,
	ReloadOutlined,
	CheckCircleOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	DollarOutlined
} from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import axios from 'axios'

// Реактивные данные
const isLoading = ref(false)
const orders = ref([])
const isUpdatingStatus = ref(false)

// Статусы заказов с переводами
const statusMap = {
	pending: { label: 'Ожидает оплаты', color: 'orange', icon: ClockCircleOutlined },
	paid: { label: 'Оплачен', color: 'green', icon: CheckCircleOutlined },
	completed: { label: 'Выполнен', color: 'blue', icon: CheckCircleOutlined },
	cancelled: { label: 'Отменен', color: 'red', icon: CloseCircleOutlined },
	failed: { label: 'Ошибка оплаты', color: 'red', icon: CloseCircleOutlined }
}

const columns = [
	{
		title: 'ID',
		dataIndex: 'id',
		key: 'id',
		width: 80,
		sorter: (a, b) => a.id - b.id
	},
	{
		title: 'Номер заказа',
		dataIndex: 'orderNumber',
		key: 'orderNumber',
		width: 150,
		ellipsis: true
	},
	{
		title: 'Пользователь',
		key: 'user',
		width: 200,
		ellipsis: true
	},
	{
		title: 'Конкурс',
		key: 'competition',
		width: 200,
		ellipsis: true
	},
	{
		title: 'Товар',
		key: 'product',
		width: 200,
		ellipsis: true
	},
	{
		title: 'Цена',
		dataIndex: 'price',
		key: 'price',
		width: 120,
		sorter: (a, b) => parseFloat(a.price) - parseFloat(b.price)
	},
	{
		title: 'Статус',
		key: 'status',
		width: 150,
		filters: Object.keys(statusMap).map(key => ({
			text: statusMap[key].label,
			value: key
		})),
		onFilter: (value, record) => record.status === value
	},
	{
		title: 'Дата создания',
		key: 'createdAt',
		width: 150,
		sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
	},
	{
		title: 'Дата оплаты',
		key: 'paidAt',
		width: 150
	},
	{
		title: 'Действия',
		key: 'actions',
		width: 120,
		fixed: 'right'
	}
]

// Методы
async function loadOrders() {
	isLoading.value = true
	try {
		const response = await axios.get('/api/orders?order[id]=desc')
		orders.value = response.data.filter(order => order && order.id)
		console.log('Загруженные заказы:', orders.value)
	} catch (error) {
		console.error('Ошибка загрузки заказов:', error)
		message.error('Ошибка загрузки заказов')
		orders.value = []
	} finally {
		isLoading.value = false
	}
}

async function updateOrderStatus(orderId, newStatus) {
	isUpdatingStatus.value = true
	try {
		await axios.patch(`/api/orders/${orderId}`,
			{ status: newStatus },
			{
				headers: {
					'Content-Type': 'application/merge-patch+json'
				}
			}
		)
		message.success('Статус заказа успешно обновлен')
		await loadOrders()
	} catch (error) {
		console.error('Ошибка обновления статуса:', error)
		message.error('Ошибка обновления статуса заказа')
	} finally {
		isUpdatingStatus.value = false
	}
}

function formatDate(dateString) {
	if (!dateString) return '—'
	return new Date(dateString).toLocaleString('ru-RU', {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit'
	})
}

function formatPrice(price) {
	return parseFloat(price).toLocaleString('ru-RU', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	}) + ' ₽'
}

// Вычисляемые свойства
const orderStats = computed(() => {
	const stats = {
		total: orders.value.length,
		pending: 0,
		paid: 0,
		completed: 0,
		cancelled: 0,
		totalRevenue: 0
	}
	
	orders.value.forEach(order => {
		stats[order.status] = (stats[order.status] || 0) + 1
		if (order.status === 'paid' || order.status === 'completed') {
			stats.totalRevenue += parseFloat(order.price || 0)
		}
	})
	
	return stats
})

// Lifecycle
onMounted(async () => {
	await loadOrders()
})
</script>

<template>
	<div class="orders-admin">
		<div class="orders-header">
			<h2 style="color: #333; margin-bottom: 20px;">Управление заказами</h2>
			
			<!-- Статистика -->
			<div class="stats-row">
				<a-row :gutter="16" style="margin-bottom: 24px;">
					<a-col :span="4">
						<a-card size="small" class="stat-card">
							<a-statistic
								title="Всего заказов"
								:value="orderStats.total"
								:value-style="{ color: '#1d232d' }"
							/>
						</a-card>
					</a-col>
					<a-col :span="4">
						<a-card size="small" class="stat-card pending">
							<a-statistic
								title="Ожидают оплаты"
								:value="orderStats.pending"
								:value-style="{ color: '#fa8c16' }"
							/>
						</a-card>
					</a-col>
					<a-col :span="4">
						<a-card size="small" class="stat-card paid">
							<a-statistic
								title="Оплачено"
								:value="orderStats.paid"
								:value-style="{ color: '#52c41a' }"
							/>
						</a-card>
					</a-col>
					<a-col :span="4">
						<a-card size="small" class="stat-card completed">
							<a-statistic
								title="Выполнено"
								:value="orderStats.completed"
								:value-style="{ color: '#1890ff' }"
							/>
						</a-card>
					</a-col>
					<a-col :span="4">
						<a-card size="small" class="stat-card cancelled">
							<a-statistic
								title="Отменено"
								:value="orderStats.cancelled"
								:value-style="{ color: '#f5222d' }"
							/>
						</a-card>
					</a-col>
					<a-col :span="4">
						<a-card size="small" class="stat-card revenue">
							<a-statistic
								title="Общий доход"
								:value="orderStats.totalRevenue"
								:precision="2"
								suffix="₽"
								:value-style="{ color: '#13c2c2' }"
							/>
						</a-card>
					</a-col>
				</a-row>
			</div>
			
			<!-- Кнопка обновления -->
			<a-button
				type="default"
				@click="loadOrders"
				:loading="isLoading"
				style="margin-bottom: 20px;"
			>
				<template #icon>
					<ReloadOutlined/>
				</template>
				Обновить данные
			</a-button>
		</div>
		
		<!-- Таблица заказов -->
		<a-spin :spinning="isLoading">
			<div v-if="orders.length > 0">
				<a-table
					:dataSource="orders"
					:columns="columns"
					:pagination="{
						pageSize: 20,
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} заказов`
					}"
					row-key="id"
					:scroll="{ x: 1400 }"
					size="middle"
				>
					<template #bodyCell="{ column, record }">
						<template v-if="column.key === 'user'">
							<div>
								<div style="font-weight: 600;">{{ record.user?.fio || record.user?.username || '—' }}</div>
								<div style="color: #666; font-size: 12px;">{{ record.user?.email || '—' }}</div>
							</div>
						</template>
						
						<template v-if="column.key === 'competition'">
							<div style="color: #333;">
								{{ record.competition?.name || '—' }}
							</div>
						</template>
						
						<template v-if="column.key === 'product'">
							<div>
								<div style="font-weight: 600;">{{ record.product?.name || '—' }}</div>
								<div style="color: #666; font-size: 12px;" v-if="record.product?.description">
									{{ record.product.description }}
								</div>
							</div>
						</template>
						
						<template v-if="column.key === 'price'">
							<span style="font-weight: 600; color: #52c41a;">
								{{ formatPrice(record.price) }}
							</span>
						</template>
						
						<template v-if="column.key === 'status'">
							<a-tag
								:color="statusMap[record.status]?.color || 'default'"
								style="border: none; font-weight: 500;"
							>
								<component
									:is="statusMap[record.status]?.icon || ClockCircleOutlined"
									style="margin-right: 4px;"
								/>
								{{ statusMap[record.status]?.label || record.status }}
							</a-tag>
						</template>
						
						<template v-if="column.key === 'createdAt'">
							<span style="color: #666;">{{ formatDate(record.createdAt) }}</span>
						</template>
						
						<template v-if="column.key === 'paidAt'">
							<span style="color: #666;">{{ formatDate(record.paidAt) }}</span>
						</template>
						
						<template v-if="column.key === 'actions'">
							<a-space>
								<a-dropdown :trigger="['click']">
									<a-button
										type="link"
										size="small"
										style="color: #F69400; padding: 4px 8px;"
									>
										<template #icon>
											<EditOutlined/>
										</template>
										Статус
									</a-button>
									<template #overlay>
										<a-menu @click="(e) => updateOrderStatus(record.id, e.key)">
											<a-menu-item
												v-for="(statusInfo, statusKey) in statusMap"
												:key="statusKey"
												:disabled="record.status === statusKey || isUpdatingStatus"
											>
												<component
													:is="statusInfo.icon"
													style="margin-right: 8px;"
												/>
												{{ statusInfo.label }}
											</a-menu-item>
										</a-menu>
									</template>
								</a-dropdown>
							</a-space>
						</template>
					</template>
				</a-table>
			</div>
		</a-spin>
		
		<!-- Пустое состояние -->
		<div v-if="!isLoading && orders.length === 0" class="empty-state">
			<a-empty description="Заказы не найдены">
				<template #image>
					<DollarOutlined style="font-size: 48px; color: #d9d9d9;" />
				</template>
			</a-empty>
		</div>
	</div>
</template>

<style scoped>
.orders-admin {
	min-height: 400px;
}

.orders-header {
	margin-bottom: 20px;
}

.stats-row {
	margin-bottom: 24px;
}

.stat-card {
	text-align: center;
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
	transition: all 0.3s;
}

.stat-card:hover {
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
	transform: translateY(-2px);
}

.stat-card.pending {
	border-left: 4px solid #fa8c16;
}

.stat-card.paid {
	border-left: 4px solid #52c41a;
}

.stat-card.completed {
	border-left: 4px solid #1890ff;
}

.stat-card.cancelled {
	border-left: 4px solid #f5222d;
}

.stat-card.revenue {
	border-left: 4px solid #13c2c2;
}

.empty-state {
	text-align: center;
	padding: 60px 20px;
	background: #fafafa;
	border-radius: 8px;
	margin-top: 20px;
}

/* Стили таблицы */
:deep(.ant-table) {
	background: #fff;
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

:deep(.ant-table-thead > tr > th) {
	background: linear-gradient(135deg, #F7C100 0%, #F69400 100%);
	color: #fff;
	font-weight: 600;
	text-align: center;
	border: none;
	padding: 16px 12px;
}

:deep(.ant-table-tbody > tr > td) {
	background: #fff;
	color: #333;
	border-bottom: 1px solid #f0f0f0;
	text-align: center;
	padding: 12px;
}

:deep(.ant-table-tbody > tr:hover > td) {
	background: #fff7e6 !important;
}

:deep(.ant-table-tbody > tr:nth-child(even) > td) {
	background: #fafafa;
}

:deep(.ant-table-tbody > tr:nth-child(even):hover > td) {
	background: #fff7e6 !important;
}

/* Стили тегов статусов */
:deep(.ant-tag) {
	display: inline-flex;
	align-items: center;
	padding: 4px 12px;
	border-radius: 16px;
	font-size: 12px;
	font-weight: 500;
}

/* Стили кнопок */
:deep(.ant-btn-link) {
	padding: 4px 8px;
	height: auto;
	font-size: 13px;
}

:deep(.ant-btn-link:hover) {
	text-decoration: none;
}

/* Стили статистических карт */
:deep(.ant-statistic-title) {
	font-size: 12px;
	font-weight: 500;
	color: #666;
	margin-bottom: 8px;
}

:deep(.ant-statistic-content) {
	font-size: 20px;
	font-weight: 600;
}

/* Dropdown меню */
:deep(.ant-dropdown-menu-item) {
	display: flex;
	align-items: center;
	padding: 8px 16px;
}

:deep(.ant-dropdown-menu-item:hover) {
	background: #fff7e6;
}

/* Спиннер */
:deep(.ant-spin-dot) {
	color: #F69400;
}

/* Empty состояние */
:deep(.ant-empty-description) {
	color: #999;
}

:deep(.ant-empty-image) {
	opacity: 0.6;
}

/* Пагинация */
:deep(.ant-pagination) {
	margin-top: 24px;
	text-align: center;
}
</style>